/**
 * 데이터 도구 공유 결정 모듈 — Excel "데이터" 탭(중복 제거 · 텍스트 나누기 ·
 * 부분합) 시맨틱의 결정적(deterministic) 핵심.
 *
 * 세 소비자가 이 규칙을 공유한다:
 *   1) ExcelRenderer(workspace-ui) — 수동 편집 UI(컨텍스트 메뉴/대화상자)
 *   2) docAgent applyPatch(askyourai) — removeDuplicates/textToColumns/subtotal 리듀서
 *   3) ai xlsx session(Python) — 에이전트 도구 미러(reimplement + 패리티 벡터로 1:1 잠금)
 *
 * (1)(2)는 이 모듈을 직접 import 하므로 항상 동일하고, (3) Python 은 규칙을
 * 재구현하되 dataTools.vectors.json 패리티 벡터 테스트로 결과 일치를 강제한다.
 *
 * 이 모듈은 "무엇을 유지/삽입/분할하는가"의 순수 계획(plan)만 계산한다. 실제 셀
 * 재배치·구조 삽입·서식 처리는 각 소비자가 자신의 셀 모델에 적용한다(정렬의
 * sortCompare 와 동일한 역할 분담). 로케일 의존 비교(localeCompare)는 쓰지 않는다.
 */

/** 데이터 도구가 다루는 원시 셀 값(레이어 무관). */
export type DataToolCellValue = string | number | boolean | null | undefined;

/* ================================================================== */
/* 1. 중복 제거(Remove Duplicates)                                      */
/* ================================================================== */

/**
 * 중복 비교용 값 정규화 — Excel "중복 제거"의 값 비교 규칙.
 *  - null/undefined/'' → '' (빈 값끼리는 동일)
 *  - number(유한) → 표준 숫자 문자열(String(n); 1.0→"1", -0→"0")
 *  - boolean → 'true'/'false'
 *  - string → 소문자화(대소문자 무시). 앞뒤 공백은 보존(Excel 은 trim 안 함).
 *  - 숫자로 파싱되는 문자열도 숫자로 정규화해 number 1 과 text "1" 을 같게 본다
 *    (Excel 의 값 비교 관대성과 일치, 결정적).
 */
export function canonicalDedupValue(raw: DataToolCellValue): string {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  if (typeof raw === 'number') return Number.isFinite(raw) ? normalizeNumberStr(raw) : '';
  const s = String(raw);
  if (s === '') return '';
  // 숫자로 파싱되면 숫자 정규화(공백만 있는 문자열은 숫자 아님)
  if (s.trim() !== '') {
    const n = Number(s);
    if (Number.isFinite(n)) return normalizeNumberStr(n);
  }
  return s.toLowerCase();
}

function normalizeNumberStr(n: number): string {
  // -0 → "0"
  return Object.is(n, -0) ? '0' : String(n);
}

/** 한 행(keyCols 순서의 값들)을 비교 키 문자열로. 널 구분자로 충돌 방지. */
export function dedupRowKey(keyValues: DataToolCellValue[]): string {
  return keyValues.map(canonicalDedupValue).join('\u0000');
}

export interface RemoveDuplicatesPlan {
  /** 유지할 본문 행 오프셋(0-based, 입력 rows 인덱스) — 원래 순서 유지. */
  keepRowOffsets: number[];
  /** 제거된 행 수. */
  removedCount: number;
}

/**
 * rows[i] = i번째 본문 행의 keyCols 값들(순서 = keyCols). 첫 등장만 유지하고
 * 이후 같은 키의 행은 제거한다. 반환은 유지 오프셋(원래 순서) + 제거 수.
 */
export function planRemoveDuplicates(rows: DataToolCellValue[][]): RemoveDuplicatesPlan {
  const seen = new Set<string>();
  const keepRowOffsets: number[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    const key = dedupRowKey(rows[i]);
    if (seen.has(key)) continue;
    seen.add(key);
    keepRowOffsets.push(i);
  }
  return { keepRowOffsets, removedCount: rows.length - keepRowOffsets.length };
}

/* ================================================================== */
/* 2. 텍스트 나누기(Text to Columns) — 구분자 모드                       */
/* ================================================================== */

export type TextToColumnsDelimiter = 'comma' | 'tab' | 'semicolon' | 'space' | 'custom';

/** 구분자 종류 → 실제 구분 문자열. custom 은 customDelimiter(빈 값이면 콤마 폴백). */
export function resolveDelimiter(d: TextToColumnsDelimiter, custom?: string): string {
  switch (d) {
    case 'comma': return ',';
    case 'tab': return '\t';
    case 'semicolon': return ';';
    case 'space': return ' ';
    case 'custom': return custom && custom.length > 0 ? custom : ',';
    default: return ',';
  }
}

export interface SplitOptions {
  /** 연속 구분자를 하나로(빈 조각 제거). */
  consecutiveAsOne?: boolean;
  /** 텍스트 한정자(기본 '"'). 단일 문자 구분자에만 적용. 빈 문자열이면 비활성. */
  qualifier?: string;
}

/**
 * 텍스트를 구분자로 분할한다(Excel 텍스트 나누기 구분자 모드).
 *  - 단일 문자 구분자: 텍스트 한정자("...") 안의 구분자는 리터럴, 이중 한정자("")는
 *    리터럴 한정자 1개. (표준 CSV)
 *  - 다중 문자 구분자(custom): 한정자 없이 단순 substring 분할.
 *  - consecutiveAsOne: 분할 후 빈 조각을 제거(양끝 포함).
 * 빈 문자열 입력은 [''] 를 반환(원 셀에 빈 값 유지). 최소 1개 조각 보장.
 */
export function splitDelimited(text: string, delim: string, opts: SplitOptions = {}): string[] {
  if (delim === '') return [text];
  const qualifier = opts.qualifier === undefined ? '"' : opts.qualifier;
  let parts: string[];
  if (delim.length === 1 && qualifier.length === 1) {
    parts = splitSingleCharWithQualifier(text, delim, qualifier);
  } else {
    parts = splitMultiChar(text, delim);
  }
  if (opts.consecutiveAsOne) {
    parts = parts.filter((p) => p !== '');
    if (parts.length === 0) parts = [''];
  }
  return parts;
}

function splitSingleCharWithQualifier(text: string, delim: string, q: string): string[] {
  const parts: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQ) {
      if (ch === q) {
        if (text[i + 1] === q) { cur += q; i += 1; } // 이스케이프된 한정자
        else inQ = false;
      } else {
        cur += ch;
      }
    } else if (ch === q) {
      inQ = true;
    } else if (ch === delim) {
      parts.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  parts.push(cur);
  return parts;
}

function splitMultiChar(text: string, delim: string): string[] {
  return text.split(delim);
}

/**
 * 분할 조각의 값 강제 변환(Excel General 형식 근사). 앞뒤 공백을 제거한 형태가
 * 유한 숫자면 number, 아니면 원문 문자열 유지(공백 보존). 지수/부호/소수 허용.
 */
export function coerceSplitValue(part: string): string | number {
  const t = part.trim();
  if (t === '') return part;
  // 순수 숫자 리터럴만(공백 포함 문자열 오탐 방지). Number 는 '0x'·'Infinity' 등도
  // 파싱하므로 정규식으로 십진 리터럴만 허용.
  if (/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(t)) {
    const n = Number(t);
    if (Number.isFinite(n)) return n;
  }
  return part;
}

/* ================================================================== */
/* 3. 부분합(Subtotal)                                                  */
/* ================================================================== */

export type SubtotalFunc =
  | 'sum' | 'count' | 'average' | 'max' | 'min'
  | 'product' | 'countNums' | 'stdDev' | 'stdDevp' | 'var' | 'varp';

/**
 * SUBTOTAL 함수 코드(1~11). Excel 부분합 도구가 사용하는 기본 코드(수동 숨김
 * 행 포함). count=COUNTA(3), countNums=COUNT(2).
 */
export function subtotalFuncCode(f: SubtotalFunc): number {
  switch (f) {
    case 'average': return 1;
    case 'countNums': return 2;
    case 'count': return 3; // COUNTA
    case 'max': return 4;
    case 'min': return 5;
    case 'product': return 6;
    case 'stdDev': return 7;
    case 'stdDevp': return 8;
    case 'sum': return 9;
    case 'var': return 10;
    case 'varp': return 11;
    default: return 9;
  }
}

/** 요약 행 라벨("{값} 요약") — Excel 한국어. 빈 값은 "(비어 있음) 요약". */
export function subtotalSummaryLabel(rawGroupValue: DataToolCellValue): string {
  const s =
    rawGroupValue === null || rawGroupValue === undefined || rawGroupValue === ''
      ? '(비어 있음)'
      : String(rawGroupValue);
  return `${s} 요약`;
}

/** 총합계 행 라벨 — Excel 한국어. */
export const SUBTOTAL_GRAND_LABEL = '총합계';

export interface SubtotalGroup {
  /** 그룹 시작 본문 행 오프셋(0-based, groupValues 인덱스). */
  startOffset: number;
  /** 그룹 끝 본문 행 오프셋(0-based, 포함). */
  endOffset: number;
  /** 라벨용 원본 그룹 값(런의 첫 행 값). */
  value: DataToolCellValue;
}

/**
 * groupValues(본문 각 행의 groupBy 열 값)를 연속 런으로 묶는다. 값 비교는
 * canonicalDedupValue 로 정규화(정렬 전제 — Excel 은 사전 정렬을 요구). 빈 입력은
 * []. 각 그룹은 [start,end] 오프셋 + 라벨용 원본 값(첫 행).
 */
export function planSubtotalGroups(groupValues: DataToolCellValue[]): SubtotalGroup[] {
  const groups: SubtotalGroup[] = [];
  if (groupValues.length === 0) return groups;
  let start = 0;
  let prevKey = canonicalDedupValue(groupValues[0]);
  for (let i = 1; i < groupValues.length; i += 1) {
    const key = canonicalDedupValue(groupValues[i]);
    if (key !== prevKey) {
      groups.push({ startOffset: start, endOffset: i - 1, value: groupValues[start] });
      start = i;
      prevKey = key;
    }
  }
  groups.push({ startOffset: start, endOffset: groupValues.length - 1, value: groupValues[start] });
  return groups;
}
